/**
 * TheWeekSection — Feb 2026 compact rebuild.
 *
 * Per the owner's competitor reference: section now sits side-by-side
 * with a looping camp video so the page feels more dynamic and a lot
 * less "stack of grids".
 *
 *   Left column   ── eyebrow + headline + intro + a vertical 5-day
 *                    rotation list (one row per day, colored icon dot,
 *                    Day + Subject + a one-line takeaway).
 *   Right column  ── autoplaying, looping, muted camp video.
 *
 * The previous 5-card pillar detail grid is gone — the day rotation
 * list communicates the same info in ~1/3 the vertical space.
 *
 * On mobile the columns stack: copy → video → day list. The video
 * uses `playsInline` so it stays inline on iOS instead of going
 * fullscreen on first tap.
 */
import { useEffect, useRef, useState } from 'react';
import {
  FlaskConical, Code, Cog, Calculator, Bot, ArrowDown,
} from 'lucide-react';
import { Button } from '../ui/button';
import SectionHeading from './SectionHeading';

// iOS Safari has a long history of refusing to autoplay even with all
// the right HTML attributes set. Per the owner's request, on iOS we
// render a single still frame extracted from the video (via Cloudinary's
// video → image transformation) instead of the <video> element itself.
// Other browsers (desktop Chrome / Firefox / Safari, Android Chrome)
// continue to render the autoplaying loop.
const VIDEO_BASE = 'https://res.cloudinary.com/dfa8raimq/video/upload';
const VIDEO_VERSION = 'v1782416417/LQ_Website_Version_vsu374';
// Keep the URL transformations minimal — earlier experiments mixing
// `f_auto` with explicit codec hints (`vc_h264,ac_aac`) returned files
// that browsers failed to decode (NETWORK_NO_SOURCE). Cloudinary's
// `q_auto` already targets H.264 for mp4 delivery, which is what every
// modern desktop browser expects.
const VIDEO_SRC = `${VIDEO_BASE}/q_auto,w_1280/${VIDEO_VERSION}.mp4`;
// Poster + iOS fallback — Cloudinary plucks a real frame at second 2.
const VIDEO_POSTER = `${VIDEO_BASE}/so_2,w_1200,c_fill,q_auto,f_auto/${VIDEO_VERSION}.jpg`;

/** Detects iOS (iPhone/iPad/iPod). Strictly UA-based — earlier we also
 *  tried a `MacIntel + maxTouchPoints > 1` fallback for iPadOS 13+ but
 *  that turned out to false-positive on some desktop browsers (Linux
 *  Chromium reports MacIntel platform with touch points present),
 *  which meant non-iOS users wrongly got the image fallback. UA-only
 *  is good enough for current iPhone/iPad models. */
function isIOSDevice() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent || '');
}

const PILLARS = [
  { id: 'science',     day: 'Monday',    name: 'Science',     icon: FlaskConical, color: '#ef4444', tint: 'bg-rose-50',    take: 'Take-home experiment kit' },
  { id: 'technology',  day: 'Tuesday',   name: 'Technology',  icon: Code,         color: '#3b82f6', tint: 'bg-blue-50',    take: 'A project they can show off' },
  { id: 'engineering', day: 'Wednesday', name: 'Engineering', icon: Cog,          color: '#10b981', tint: 'bg-emerald-50', take: 'Hands-on prototype every day' },
  { id: 'math',        day: 'Thursday',  name: 'Math',        icon: Calculator,   color: '#8b5cf6', tint: 'bg-violet-50',  take: 'Problem-solving confidence' },
  { id: 'robotics',    day: 'Friday',    name: 'Robotics',    icon: Bot,          color: '#f59e0b', tint: 'bg-amber-50',   take: 'Team build & friendly challenge' },
];

export default function TheWeekSection() {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  // SSR-safe iOS detection — runs once on the client after hydration.
  const [isIOS, setIsIOS] = useState(false);
  useEffect(() => { setIsIOS(isIOSDevice()); }, []);

  // Reveal the day rows in a staggered cascade once the section enters
  // view. Pure CSS transitions driven from the `revealed` state.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || revealed) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setRevealed(true),
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [revealed]);

  // Safari/iOS occasionally drops the autoplay even with `autoPlay+muted`
  // attributes set — React has a known quirk where the `muted` JSX prop
  // doesn't always reflect to the DOM attribute on first render, and
  // mobile Safari checks the DOM attribute (not the JS property) before
  // allowing autoplay. Force both, then kick play(), then back off
  // silently if the browser still blocks it.
  //
  // Per Apple's WebKit docs: "Playback stops if the video element isn't
  // visible onscreen or is out of the viewport." So we also wire an
  // IntersectionObserver and re-call play() every time the element
  // re-enters view — otherwise the video stays paused for users who
  // scroll past quickly and back.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();

    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) tryPlay(); }),
      { threshold: 0.25 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  const scrollToCamps = () => {
    const el = document.getElementById('browse-camps');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      ref={sectionRef}
      id="how-camps-work"
      className="bg-rsc-slate-soft py-16 sm:py-20 font-poppins scroll-mt-32"
      data-testid="the-week-section"
    >
      <div className="container-app">
        <SectionHeading
          eyebrow="How camp works"
          title="Five disciplines."
          accent="One epic week."
          body="Every camper rotates through all five STEM pillars, one each day, Monday to Friday. No two days look the same, and Friday always ends on robotics."
        />

        {/* Two-column body: day list on the left, looping video on the right. */}
        <div className="mt-10 sm:mt-12 grid md:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 items-stretch max-w-6xl mx-auto">

          {/* ─── LEFT: vertical day rotation ─── */}
          <ol className="space-y-2.5" data-testid="day-rotation-list">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <li
                  key={p.id}
                  className="group flex items-center gap-4 rounded-xl bg-white border border-rsc-card-border p-3.5 sm:p-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                  style={{
                    opacity: revealed ? 1 : 0,
                    transform: revealed ? 'translateY(0)' : 'translateY(8px)',
                    transition: `opacity 500ms ease ${i * 110}ms, transform 500ms ease ${i * 110}ms, box-shadow 200ms ease, translate 200ms ease`,
                  }}
                  data-testid={`day-row-${p.id}`}
                >
                  <span
                    className={`flex-shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl text-white shadow-sm`}
                    style={{ backgroundColor: p.color }}
                  >
                    <Icon className="w-5 h-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-rsc-charcoal-muted">
                        {p.day}
                      </span>
                      <span className="text-base font-extrabold text-rsc-brand-blue">
                        {p.name}
                      </span>
                    </div>
                    <p className="text-[12px] sm:text-[13px] text-rsc-charcoal leading-snug mt-0.5">
                      {p.take}
                    </p>
                  </div>
                </li>
              );
            })}

            {/* Inline CTA — surfaces the action right where the parent's
                attention is, instead of forcing a scroll to find one. */}
            <li className="pt-2">
              <Button
                onClick={scrollToCamps}
                className="bg-rsc-yellow text-rsc-brand-blue hover:bg-rsc-yellow/90 font-extrabold px-5 h-11 rounded-full shadow-md shadow-amber-200/60 active:scale-95 transition-all"
                data-testid="how-camps-work-cta"
              >
                See available weeks
                <ArrowDown className="ml-2 w-4 h-4" />
              </Button>
            </li>
          </ol>

          {/* ─── RIGHT: looping camp video (non-iOS) or static still
              frame (iOS Safari, which is unreliable for video autoplay
              even with every recommended attribute set). The still
              image is a real frame extracted from the same video via
              Cloudinary so the visual story stays consistent. ─── */}
          <div className="relative rounded-2xl overflow-hidden shadow-xl ring-1 ring-rsc-card-border bg-rsc-brand-blue aspect-video md:aspect-auto md:min-h-[420px] order-first md:order-last">
            {isIOS ? (
              <img
                src={VIDEO_POSTER}
                alt="A real moment from Ready Set STEM camp"
                loading="eager"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="how-camps-work-video-ios-fallback"
              />
            ) : (
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                poster={VIDEO_POSTER}
                aria-label="Ready Set STEM campers in action"
                data-testid="how-camps-work-video"
              >
                <source src={VIDEO_SRC} type="video/mp4" />
              </video>
            )}
            {/* Subtle gradient overlay along the bottom so any caption-
                style copy could be added later without re-styling. */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent"
            />
            {/* Tiny "loop" pill — quiet visual cue that this is a
                showreel, not a single play-once trailer. */}
            <span className="pointer-events-none absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/40 backdrop-blur text-[10px] uppercase tracking-widest font-bold text-white">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-rsc-yellow animate-pulse" />
              Live from camp
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
